import { useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Paper,
} from '@mui/material';
import { DeleteOutlineOutlined, EditOutlined, Telegram } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EntryCategoryLabels, type Entry } from '@lorrainemarwood-care/shared';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import EntryEditDialog from './EntryEditDialog';

dayjs.extend(relativeTime);

const CATEGORY_COLOR: Record<Entry['category'], 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error'> = {
  med: 'primary',
  symptom: 'warning',
  note: 'default',
  handover: 'info',
  meal: 'success',
  appointment: 'secondary',
};

export default function EntryList({ entries }: { entries: Entry[] }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Entry | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/entries/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entries'] }),
  });

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
        No entries yet.
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={1.5}>
        {entries.map((e) => {
          const canEdit = user?.id === e.authorId || user?.role === 'admin';
          return (
            <Paper key={e.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip
                      size="small"
                      label={EntryCategoryLabels[e.category]}
                      color={CATEGORY_COLOR[e.category]}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {e.authorName} · {dayjs(e.occurredAt).fromNow()} · {dayjs(e.occurredAt).format('DD MMM HH:mm')}
                    </Typography>
                    {e.source === 'telegram' && (
                      <Telegram fontSize="small" sx={{ color: 'text.secondary' }} />
                    )}
                  </Stack>
                  {e.category === 'med' && e.medName && (
                    <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                      {e.medName}{e.medDose ? ` — ${e.medDose}` : ''}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {e.body}
                  </Typography>
                </Box>
                {canEdit && (
                  <Stack direction="row" spacing={0.25}>
                    <IconButton size="small" onClick={() => setEditing(e)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteMutation.mutate(e.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteOutlineOutlined fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <EntryEditDialog
        entry={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
